<template>
  <component
    :is="tag"
    class="base-btn"
    :class="buttonClass"
    :disabled="disabled || loading"
    :type="type"
    :href="href"
    :to="to"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- Loading Spinner -->
    <span v-if="loading" class="btn-spinner"></span>

    <!-- Leading Icon Slot -->
    <span v-if="$slots.icon && !loading" class="btn-icon btn-icon--leading">
      <slot name="icon"></slot>
    </span>

    <!-- Label -->
    <span class="btn-label">
      <slot>{{ label }}</slot>
    </span>

    <!-- Trailing Icon Slot -->
    <span v-if="$slots['icon-trailing']" class="btn-icon btn-icon--trailing">
      <slot name="icon-trailing"></slot>
    </span>
  </component>
</template>

<script>
export default {
  name: 'BaseButton',

  inheritAttrs: false,

  props: {
    label: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'ghost', 'danger', 'success'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'button'
    },
    href: {
      type: String,
      default: null
    },
    to: {
      type: [String, Object],
      default: null
    }
  },

  computed: {
    tag() {
      if (this.href) return 'a'
      if (this.to) return 'router-link'
      return 'button'
    },
    buttonClass() {
      return [
        `btn--${this.variant}`,
        `btn--${this.size}`,
        {
          'btn--full-width': this.fullWidth,
          'btn--loading': this.loading,
          'btn--disabled': this.disabled
        }
      ]
    }
  }
}
</script>

<style scoped>
.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2, 0.5rem);
  font-family: inherit;
  font-weight: var(--btn-font-weight, 600);
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all var(--transition-base, 0.2s);
  white-space: nowrap;
}

.base-btn:focus-visible {
  outline: 2px solid var(--color-primary-500, #0ea5e9);
  outline-offset: 2px;
}

/* Sizes */
.btn--sm {
  font-size: var(--text-base, 0.8125rem);
  padding: var(--btn-padding-y-sm, 0.5rem) var(--btn-padding-x-sm, 0.875rem);
  border-radius: var(--radius-md, 0.375rem);
}

.btn--md {
  font-size: var(--text-md, 0.875rem);
  padding: var(--btn-padding-y, 0.75rem) var(--btn-padding-x, 1.25rem);
  border-radius: var(--btn-radius, 0.5rem);
}

.btn--lg {
  font-size: var(--text-lg, 0.9375rem);
  padding: 0.875rem 1.5rem;
  border-radius: var(--btn-radius, 0.5rem);
}

/* Full Width */
.btn--full-width {
  width: 100%;
}

/* Variants */
.btn--primary {
  background: var(--color-gray-900, #18181b);
  color: var(--text-inverse, #ffffff);
}

.btn--primary:hover:not(:disabled) {
  background: var(--color-gray-800, #27272a);
}

.btn--secondary {
  background: var(--color-primary-500, #0ea5e9);
  color: var(--text-inverse, #ffffff);
}

.btn--secondary:hover:not(:disabled) {
  background: var(--color-primary-600, #0284c7);
}

.btn--outline {
  background: transparent;
  color: var(--text-primary, #18181b);
  border: 1px solid var(--border-color, #e4e4e7);
}

.btn--outline:hover:not(:disabled) {
  background: var(--bg-hover, #f4f4f5);
  border-color: var(--color-gray-400, #a1a1aa);
}

.btn--ghost {
  background: transparent;
  color: var(--text-muted, #71717a);
}

.btn--ghost:hover:not(:disabled) {
  background: var(--bg-hover, #f4f4f5);
  color: var(--text-primary, #18181b);
}

.btn--danger {
  background: var(--color-error-600, #dc2626);
  color: var(--text-inverse, #ffffff);
}

.btn--danger:hover:not(:disabled) {
  background: var(--color-error-700, #b91c1c);
}

.btn--success {
  background: var(--color-success-500, #10b981);
  color: var(--text-inverse, #ffffff);
}

.btn--success:hover:not(:disabled) {
  background: var(--color-success-600, #059669);
}

/* Disabled State */
.base-btn:disabled,
.btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading State */
.btn--loading {
  cursor: wait;
}

/* Spinner */
.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.btn--lg .btn-spinner {
  width: 18px;
  height: 18px;
}

.btn--sm .btn-spinner {
  width: 14px;
  height: 14px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Icons */
.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon svg {
  width: var(--icon-sm, 1rem);
  height: var(--icon-sm, 1rem);
}

.btn--lg .btn-icon svg {
  width: var(--icon-md, 1.25rem);
  height: var(--icon-md, 1.25rem);
}

/* Label */
.btn-label {
  display: inline-flex;
  align-items: center;
}
</style>
